import {
  createFileRoute,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  resolvePostAuthRedirect,
  takePostSsoRedirect,
} from "@/lib/ssoRedirect";
import type { PortalSession } from "@/types/portal";

type CallbackSearchParams = {
  accessToken?: string;
  refreshToken?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  role?: string;
  roleLevel?: string;
  error?: string;
  redirect?: string;
};

export const Route = createFileRoute("/auth/callback")({
  component: AuthCallbackPage,
  validateSearch: (search: Record<string, unknown>): CallbackSearchParams => ({
    accessToken:
      typeof search.accessToken === "string" ? search.accessToken : undefined,
    refreshToken:
      typeof search.refreshToken === "string" ? search.refreshToken : undefined,
    email: typeof search.email === "string" ? search.email : undefined,
    firstName:
      typeof search.firstName === "string" ? search.firstName : undefined,
    lastName: typeof search.lastName === "string" ? search.lastName : undefined,
    role: typeof search.role === "string" ? search.role : undefined,
    roleLevel:
      typeof search.roleLevel === "string" ? search.roleLevel : undefined,
    error: typeof search.error === "string" ? search.error : undefined,
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
  }),
});

function AuthCallbackPage() {
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const search = useSearch({ from: "/auth/callback" });
  const [status, setStatus] = useState<"processing" | "error">("processing");
  // The destination, resolved with a NON-DESTRUCTIVE read so that running it
  // twice cannot change the answer.
  //
  // Both review chairs measured the same thing independently: React
  // StrictMode double-invokes a `useState` initializer exactly as it does an
  // effect, so an earlier version that consumed the stash here was just as
  // broken as consuming it in the effect — the second pass saw an empty stash
  // and fell back to the dashboard. Production was never affected (StrictMode
  // is dev-only) but every developer testing SSO locally would watch the fix
  // "not work", and "it only misbehaves in dev" is how the consent page's
  // 60-deep redirect nest survived review once already.
  //
  // The single consumption happens in the effect, immediately before
  // navigating. Running THAT twice clears an already-empty stash and
  // navigates twice to the same captured path: both idempotent.
  const [redirectPath] = useState(() =>
    resolvePostAuthRedirect(search.redirect, { consume: false }),
  );
  const [errorMessage, setErrorMessage] = useState<string>("");

  useEffect(() => {
    if (search.error) {
      setStatus("error");
      setErrorMessage(decodeURIComponent(search.error));
      return;
    }

    if (!search.accessToken || !search.email) {
      setStatus("error");
      setErrorMessage(
        "Invalid authentication callback. Missing required parameters.",
      );
      return;
    }

    // Create session from callback params
    const session: PortalSession = {
      token: search.accessToken,
      refreshToken: search.refreshToken ?? "",
      user: {
        email: search.email,
        firstName: search.firstName,
        lastName: search.lastName,
        role: search.role,
        roleLevel: search.roleLevel
          ? parseInt(search.roleLevel, 10)
          : undefined,
      },
      createdAt: Date.now(),
    };

    setSession(session);

    // `redirectPath` was resolved at mount (see above). Spend the stash now,
    // so a later unrelated sign-in cannot inherit this destination.
    takePostSsoRedirect();
    navigate({ to: redirectPath, replace: true });
  }, [search, setSession, navigate, redirectPath]);

  if (status === "error") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="w-full max-w-md rounded-2xl border border-red-200 bg-white p-8 shadow-lg">
          <h1 className="text-xl font-bold text-red-700">
            Authentication Error
          </h1>
          <p className="mt-2 text-sm text-slate-600">{errorMessage}</p>
          <button
            className="mt-6 w-full rounded-xl bg-brand-blue-medium px-4 py-3 text-sm font-semibold text-white hover:bg-brand-blue-dark"
            onClick={() => navigate({ to: "/portal/sign-in" })}
          >
            Return to Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50">
      <div className="flex flex-col items-center gap-4">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-blue-medium border-t-transparent" />
        <p className="text-sm text-slate-500">Completing sign in...</p>
      </div>
    </div>
  );
}
